import React, { useEffect, useState } from 'react';
import { isAppShellStale, peekAppVersion } from '../api/versionService';
import { toast } from '../utilities/appToast';
import { useIsMobile } from '../hooks/useIsMobile';
import './MobileLayout.css';

const PERMISSION_DISMISS_KEY = 'mobile-notif-permission-dismissed';
const VERSION_NOTICE_KEY = 'pwa-version-notice';
const RELOAD_GUARD_KEY = 'pwa-update-reload';
const VERSION_CHECK_MS = 60 * 1000;

const notificationsAvailable = () => typeof window !== 'undefined' && 'Notification' in window;

const showUpdatedNotice = (version) => {
    const label = version && version !== 'nueva' ? ` (${version})` : '';
    const message = `Salió una versión nueva y la aplicación ya se actualizó${label}.`;
    toast.success(message, { autoClose: 8000 });

    if (notificationsAvailable() && Notification.permission === 'granted') {
        try {
            new Notification('Marketplace actualizado', {
                body: message,
                icon: '/icons/icon-192.png',
            });
        } catch {
            /* el aviso en pantalla ya informó la actualización */
        }
    }
};

const applyUpdate = async (version) => {
    const attempts = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || '0');
    if (attempts >= 2) return;

    sessionStorage.setItem(RELOAD_GUARD_KEY, String(attempts + 1));
    sessionStorage.setItem(VERSION_NOTICE_KEY, version || 'nueva');
    if (version && version !== 'nueva') {
        localStorage.setItem('appVersion', version);
    }

    if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const waiting = registrations.map((registration) => registration.waiting).find(Boolean);
        if (waiting) {
            waiting.postMessage({ type: 'SKIP_WAITING' });
            return;
        }

        if (window.caches) {
            const keys = await caches.keys();
            await Promise.all(
                keys
                    .filter((key) => key === 'html-shell' || key.includes('precache') || key.startsWith('workbox'))
                    .map((key) => caches.delete(key))
            );
        }
    }

    window.location.reload();
};

export default function MobileUpdates() {
    const isMobile = useIsMobile();
    const [askPermission, setAskPermission] = useState(false);

    useEffect(() => {
        const pending = sessionStorage.getItem(VERSION_NOTICE_KEY);
        if (!pending) return;
        sessionStorage.removeItem(VERSION_NOTICE_KEY);
        const timer = setTimeout(() => showUpdatedNotice(pending), 400);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!isMobile || !notificationsAvailable()) return;
        if (Notification.permission !== 'default') return;
        try {
            if (localStorage.getItem(PERMISSION_DISMISS_KEY) === '1') return;
        } catch {
            return;
        }
        setAskPermission(true);
    }, [isMobile]);

    useEffect(() => {
        let stopped = false;

        const checkVersion = async () => {
            if (stopped || sessionStorage.getItem(VERSION_NOTICE_KEY)) return;

            const [remote, staleShell] = await Promise.all([
                peekAppVersion(),
                isAppShellStale(),
            ]);
            if (stopped) return;

            if (!staleShell) {
                sessionStorage.removeItem(RELOAD_GUARD_KEY);
            }

            const local = localStorage.getItem('appVersion');
            if (remote && !local) {
                localStorage.setItem('appVersion', remote);
            } else if (remote && local !== remote && !staleShell) {
                localStorage.setItem('appVersion', remote);
                showUpdatedNotice(remote);
            }

            if (staleShell) {
                await applyUpdate(remote || local || 'nueva');
            }
        };

        checkVersion();
        const timer = setInterval(checkVersion, VERSION_CHECK_MS);
        const onVisible = () => {
            if (document.visibilityState === 'visible') checkVersion();
        };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            stopped = true;
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    if (!askPermission) return null;

    const dismiss = () => {
        setAskPermission(false);
        try {
            localStorage.setItem(PERMISSION_DISMISS_KEY, '1');
        } catch {
            /* si no se puede guardar, el aviso vuelve en la próxima visita */
        }
    };

    const allow = async () => {
        try {
            await Notification.requestPermission();
        } catch {
            /* el navegador puede rechazar el pedido fuera de un gesto del usuario */
        }
        dismiss();
    };

    return (
        <div className="mobile-notify" role="dialog" aria-label="Permiso de notificaciones">
            <p className="mobile-notify__text">
                Activá las notificaciones para avisarte cuando salga una versión nueva y la aplicación se actualice.
            </p>
            <button type="button" className="mobile-notify__action" onClick={allow}>
                Permitir
            </button>
            <button type="button" className="mobile-notify__dismiss" onClick={dismiss}>
                Ahora no
            </button>
        </div>
    );
}
